import { Route, Routes, Navigate } from "react-router-dom";

import "./App.css";
import "./pages/SubmitJobs/ImportForm.css";
import { Fade, useMediaQuery } from "@mui/material";
import React, { Suspense } from "react";
import { CustomToolbar, CustomTitle, DrawerNavigation, NavLink, SIDEBAR_COLLAPSED_WIDTH } from "./widgets/Toolbar";
import { LoginForm, Logout, Register, PasswordReset, RegistrationContactNotice } from "./pages/Authentication";
import { browserTokenStore } from "@gui/infrastructure/pass-through/storage";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Sentry from "@sentry/react";
import { LoadingComponent } from "@gui/widgets/LoadingComponent";
import HomePage from "./pages/Home/Home";
import { NotFound } from "./pages/NotFound/NotFound";
import { DEFAULT_PAGE } from "@domain/constants";
import { User } from "@domain/Auth";
import { useCurrentUser } from "./useCurrentUser";
import { AppProviders, theme } from "./providers/AppProviders";

// Lazy-loaded routes — split into separate chunks
const JobsDashboardWithLocalizationProvider = React.lazy(() => import("./pages/JobsDashboard/JobsDashboard"));
const JobDetailPage = React.lazy(() =>
	import("./pages/JobsDashboard/JobDetail").then((m) => ({ default: m.JobDetailPage })),
);
const JobSubmissionForm = React.lazy(() => import("./pages/SubmitJobs/ImportForm"));
const MarkJobReadyPage = React.lazy(() =>
	import("./pages/MarkJobsReady/MarkJobReadyPage").then((m) => ({ default: m.MarkJobReadyPage })),
);
const Profile = React.lazy(() => import("./pages/Profile").then((m) => ({ default: m.Profile })));

// Admin pages — rarely visited, biggest code-splitting win
const AgrianIssues = React.lazy(() => import("./pages/Admin/AgrianIssues").then((m) => ({ default: m.AgrianIssues })));
const DataSendbackIssues = React.lazy(() =>
	import("./pages/Admin/DataSendbackIssues").then((m) => ({ default: m.DataSendbackIssues })),
);
const ExeMissionValidation = React.lazy(() =>
	import("./pages/Admin/ExeMissionValidation").then((m) => ({ default: m.ExeMissionValidation })),
);
const JobErrors = React.lazy(() => import("./pages/Admin/JobErrors").then((m) => ({ default: m.JobErrors })));
const GeoFileConverter = React.lazy(() =>
	import("./pages/Admin/GeoFileConverter").then((m) => ({ default: m.GeoFileConverter })),
);
const ImportSettings = React.lazy(() =>
	import("./pages/Admin/ImportSettings").then((m) => ({ default: m.ImportSettings })),
);
const MissionControlLogs = React.lazy(() =>
	import("./pages/Admin/MissionControlLogs").then((m) => ({ default: m.MissionControlLogs })),
);
const MissionReport = React.lazy(() =>
	import("./pages/Admin/MissionReport").then((m) => ({ default: m.MissionReport })),
);
const MissionControlPasswordsGenerator = React.lazy(() =>
	import("./pages/Admin/MissionControlPasswordsGenerator").then((m) => ({
		default: m.MissionControlPasswordsGenerator,
	})),
);
const PasswordGenerator = React.lazy(() =>
	import("./pages/Admin/PasswordGenerator").then((m) => ({ default: m.PasswordGenerator })),
);
const Robots = React.lazy(() => import("./pages/Admin/Robots").then((m) => ({ default: m.Robots })));
const JobDiagnosticsPage = React.lazy(() =>
	import("./pages/Admin/JobDiagnostics").then((m) => ({ default: m.JobDiagnosticsPage })),
);
const DealCreator = React.lazy(() => import("./pages/Admin/DealCreator").then((m) => ({ default: m.DealCreator })));
const DealMatcher = React.lazy(() => import("./pages/Admin/DealMatcher").then((m) => ({ default: m.DealMatcher })));
const BranchMatcher = React.lazy(() =>
	import("./pages/Admin/BranchMatcher").then((m) => ({ default: m.BranchMatcher })),
);
const Scheduling = React.lazy(() => import("./pages/Admin/Scheduling").then((m) => ({ default: m.Scheduling })));

interface ProtectedRouteProps {
	redirectPath?: string;
}
const ProtectedRoute = ({ children, redirectPath }: React.PropsWithChildren<ProtectedRouteProps>) => {
	const jwtToken = browserTokenStore.getJwt();
	if (!jwtToken) {
		return <Navigate to={redirectPath ?? "/login"} replace />;
	}

	return children;
};

function App() {
	// User identity + load state managed by useCurrentUser. The hook owns the
	// auth-failure interceptor wiring, the user fetch, and Sentry user reporting.
	const { currentUser, loading, setCurrentUser, setLoading } = useCurrentUser();
	const [currentLink, setCurrentLink] = React.useState<NavLink>({ to: "/login", text: "Login" });
	const [mobileOpen, setMobileOpen] = React.useState(false);
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));

	const handleLogin = (user: User) => {
		setCurrentUser(user);
	};

	// ToastContainer mounts unconditionally (outside the loading ternary below) so it's
	// never racing its own first-mount queue flush against the initial user-fetch's
	// rejection — see #645 (dropped "Session expired" toast under StrictMode double-invoke).
	return (
		<>
			{loading ? (
				<div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
					<LoadingComponent />
				</div>
			) : (
				<Fade in={!loading}>
					<div>
						<AppProviders currentUser={currentUser}>
							<CustomTitle setCurrentLink={setCurrentLink} />
							<div
								style={{
									height: "100vh",
									overflow: "hidden",
									display: "flex",
									flexDirection: "column",
								}}
							>
								<CustomToolbar title={currentLink.text} onMenuClick={() => setMobileOpen(true)} />
								<DrawerNavigation
									onNavigate={(link: NavLink) => {
										setCurrentLink(link);
									}}
									mobileOpen={mobileOpen}
									onMobileClose={() => setMobileOpen(false)}
								/>

								{/* Route container flex-fills the remaining viewport height after the toolbar. The
						    previous `calc(100vh - 64px)` assumed a single-row 64px Toolbar, but on narrow
						    mobile widths the toolbar wraps to two rows (~96px), which clipped the bottom
						    32px of every page. Using flex: 1 + minHeight: 0 adapts to whatever the
						    toolbar's natural height is. */}
								<div
									style={{
										marginLeft: isMobile ? 0 : SIDEBAR_COLLAPSED_WIDTH,
										flex: 1,
										minHeight: 0,
										overflowY: "auto",
									}}
								>
									<Suspense fallback={<LoadingComponent />}>
										<Routes>
											<Route path="/home" element={<HomePage setLoggedIn={handleLogin} />} />
											<Route
												path="/login"
												element={
													currentUser ? <Navigate to={DEFAULT_PAGE} replace /> : <LoginForm setLoggedIn={handleLogin} />
												}
											/>
											<Route path="/register" element={<Register setLoggedIn={handleLogin} />} />
											<Route
												path="/"
												element={currentUser ? <Navigate to={DEFAULT_PAGE} /> : <LoginForm setLoggedIn={handleLogin} />}
											/>
											<Route path="/forgot-password" element={<PasswordReset setLoggedIn={handleLogin} />} />
											<Route path="/reset-password" element={<PasswordReset setLoggedIn={handleLogin} />} />
											<Route path="/jobs">
												<Route
													index
													path="dashboard"
													element={
														<ProtectedRoute>
															<JobsDashboardWithLocalizationProvider setLoading={setLoading} />
														</ProtectedRoute>
													}
												/>
												<Route
													path="submit"
													element={
														<ProtectedRoute>
															<JobSubmissionForm />
														</ProtectedRoute>
													}
												/>
												<Route
													path="ready"
													element={
														<ProtectedRoute>
															<MarkJobReadyPage setLoading={setLoading} />
														</ProtectedRoute>
													}
												/>
												<Route
													path=":jobId"
													element={
														<ProtectedRoute>
															<JobDetailPage />
														</ProtectedRoute>
													}
												/>
											</Route>
											<Route
												path="/profile"
												element={
													<ProtectedRoute>
														<Profile />
													</ProtectedRoute>
												}
											/>
											<Route path="/logout" element={<Logout />} />
											<Route path="/registration-notice" element={<RegistrationContactNotice />} />
											<Route path="/admin">
												<Route
													path="company-settings"
													element={
														<ProtectedRoute>
															<ImportSettings />
														</ProtectedRoute>
													}
												/>
												{/* Old path — keep existing links/bookmarks working. */}
												<Route path="import" element={<Navigate to="/admin/company-settings" replace />} />
												<Route
													path="geofile-converter"
													element={
														<ProtectedRoute>
															<GeoFileConverter />
														</ProtectedRoute>
													}
												/>
												<Route
													path="mission-control-logs"
													element={
														<ProtectedRoute>
															<MissionControlLogs />
														</ProtectedRoute>
													}
												/>
												<Route
													path="mission-control-passwords-generator"
													element={
														<ProtectedRoute>
															<MissionControlPasswordsGenerator />
														</ProtectedRoute>
													}
												/>
												<Route
													path="password-generator"
													element={
														<ProtectedRoute>
															<PasswordGenerator />
														</ProtectedRoute>
													}
												/>
												<Route
													path="agrian-issues"
													element={
														<ProtectedRoute>
															<AgrianIssues />
														</ProtectedRoute>
													}
												/>
												<Route
													path="data-sendback-issues"
													element={
														<ProtectedRoute>
															<DataSendbackIssues />
														</ProtectedRoute>
													}
												/>
												<Route
													path="exe-mission-validation"
													element={
														<ProtectedRoute>
															<ExeMissionValidation />
														</ProtectedRoute>
													}
												/>
												<Route
													path="exe-mission-validation/:jobId"
													element={
														<ProtectedRoute>
															<ExeMissionValidation />
														</ProtectedRoute>
													}
												/>
												<Route
													path="job-errors"
													element={
														<ProtectedRoute>
															<JobErrors />
														</ProtectedRoute>
													}
												/>
												<Route
													path="job-errors/:jobId"
													element={
														<ProtectedRoute>
															<JobErrors />
														</ProtectedRoute>
													}
												/>
												<Route
													path="robots"
													element={
														<ProtectedRoute>
															<Robots />
														</ProtectedRoute>
													}
												/>
												<Route
													path="robots/:robotId"
													element={
														<ProtectedRoute>
															<Robots />
														</ProtectedRoute>
													}
												/>
												<Route
													path="robots/:robotId/:shiftId"
													element={
														<ProtectedRoute>
															<Robots />
														</ProtectedRoute>
													}
												/>
												<Route
													path="job-diagnostics"
													element={
														<ProtectedRoute>
															<JobDiagnosticsPage />
														</ProtectedRoute>
													}
												/>
												<Route
													path="job-diagnostics/:jobId"
													element={
														<ProtectedRoute>
															<JobDiagnosticsPage />
														</ProtectedRoute>
													}
												/>
												<Route
													path="mission-report"
													element={
														<ProtectedRoute>
															<MissionReport />
														</ProtectedRoute>
													}
												/>
												<Route
													path="deal-matcher"
													element={
														<ProtectedRoute>
															<DealMatcher />
														</ProtectedRoute>
													}
												/>
												<Route
													path="deal-creator"
													element={
														<ProtectedRoute>
															<DealCreator />
														</ProtectedRoute>
													}
												/>
												<Route
													path="branch-matcher"
													element={
														<ProtectedRoute>
															<BranchMatcher />
														</ProtectedRoute>
													}
												/>
												<Route
													path="scheduling"
													element={
														<ProtectedRoute>
															<Scheduling />
														</ProtectedRoute>
													}
												/>
											</Route>
											{/* Catch-all: unmatched paths previously rendered a blank content area
									    (looked like a crash). See customer-portal-fjb2. */}
											<Route path="*" element={<NotFound />} />
										</Routes>
									</Suspense>
								</div>
							</div>
						</AppProviders>
					</div>
				</Fade>
			)}
			<ToastContainer position="bottom-left" />
		</>
	);
}

export default Sentry.withProfiler(App);
